import React from "react";
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Typography,
  Button,
  Chip,
  IconButton,
  Collapse,
  LinearProgress,
  CircularProgress,
  Tooltip,
} from "@mui/material";

import {
  KeyboardArrowUp,
  KeyboardArrowDown,
  SystemUpdateAltOutlined,
  OndemandVideoOutlined,
  PictureAsPdfOutlined,
  ImageOutlined,
} from "@mui/icons-material";
import { useAuthenticationConsumer } from "shared/providers/Authentication";
import { useSnackbarConsumer } from "shared/providers/Snackbar";
import { IQueuedFileDownload, IQueuedFileDownloads } from "modules/files/types";
import {
  downloadFile,
  downloadQueuedFiles,
  getPresignedURLByFileId,
} from "modules/files/services";
import { formatDate } from "shared/parsers/date";
import { Player } from "../Player";
import { toMP4 } from "./services/mp4";
import PDFViewer from "../PDFViewer";
import ImagePreview from "../ImagePreview";

const statusDisplay = {
  IN_PROGRESS: "Em progresso",
  COMPLETED: "Disponível para download",
};

const scrollTop = () => {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
};

const QueuedDownloads: React.FC = () => {
  const { readOnly } = useAuthenticationConsumer();
  const { openSnackbar } = useSnackbarConsumer();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [loadingFile, setLoadingFile] = React.useState<boolean>(false);
  const [open, setOpen] = React.useState<string>("");
  const [downloads, setDownloads] = React.useState<IQueuedFileDownloads>(
    {} as IQueuedFileDownloads
  );
  const [videoOpen, setVideoOpen] = React.useState<boolean>(false);
  const [videoName, setVideoName] = React.useState<string>("");
  const [videoURL, setVideoURL] = React.useState<string>("");
  const [loadingVideo, setLoadingVideo] = React.useState<boolean>(false);
  const [pdfURL, setPDFURL] = React.useState<string>("");
  const [imgURL, setImgURL] = React.useState<string>("");

  const handleDownloads = React.useCallback(async () => {
    setLoading(true);
    const { status, data } = await downloadQueuedFiles();

    if (status === 200) {
      setDownloads(data);
    }
    setLoading(false);
  }, []);

  const handleDownloadFile = async (videoId: number) => {
    scrollTop();
    setLoadingFile(true);
    const { data } = await downloadFile(videoId);
    if (data && data.url) {
      openSnackbar({
        message: "Iniciando download...",
        severity: "info",
      });
      setTimeout(() => {
        window.open(data.url);
        setLoadingFile(false);
      }, 1000);
    } else {
      setLoadingFile(false);
      openSnackbar({
        message: "Não foi possível efetuar o download, tente novamente.",
        severity: "error",
        autoHideDuration: 10000,
      });
    }
  };

  const resetVideoOpen = () => {
    setVideoURL("");
    setVideoName("");
    setVideoOpen(false);
    setLoading(false);
  };

  const handleVideoOpen = async (
    url: string,
    fileName: string,
    video?: IQueuedFileDownload,
    download = false,
    id?: number
  ) => {
    if (download && id) {
      const { data: dataDownload } = await getPresignedURLByFileId(id);
      openSnackbar({
        message: "Iniciando download...",
        severity: "info",
      });
      setTimeout(() => {
        window.open(decodeURIComponent(dataDownload.url));
        setLoadingFile(false);
      }, 1000);
      setLoadingVideo(false);
      return false;
    }

    scrollTop();

    if (fileName && url) {
      if (url.toLowerCase().includes(".mp4")) {
        const { data: dataMP4 } = await getPresignedURLByFileId(id);
        setVideoURL(decodeURIComponent(dataMP4.url));
        setVideoName(fileName);
        setVideoOpen(true);
        return false;
      }

      setLoadingVideo(true);

      const { status, data } = await toMP4([url]);

      if (status === 201 && data.bufferUrls[0]) {
        setVideoURL(data.bufferUrls[0]);
        setVideoName(fileName);
        setVideoOpen(true);
      } else {
        setVideoURL("");
        setVideoName("");
        setVideoOpen(false);
        setLoadingVideo(false);
        openSnackbar({
          message: "Não foi possível realizar a operação.",
          severity: "warning",
        });
      }
    }

    setLoadingVideo(false);
  };

  const handlePDFOpen = React.useCallback(async (id: number) => {
    scrollTop();
    setLoadingVideo(true);
    const { data } = await downloadFile(id);
    setPDFURL(data.url);
    setLoadingVideo(false);
  }, []);

  const handleImgOpen = React.useCallback(async (id: number) => {
    scrollTop();
    setLoadingVideo(true);
    const { data } = await downloadFile(id);
    setImgURL(data.url);
    setLoadingVideo(false);
  }, []);

  // const handleConversion = React.useCallback(
  //   async (queuedId: number) => {
  //     try {
  //       const { status, data } = await forceConversion(queuedId);
  //       if ([200, 201].includes(status) && data && data.success) {
  //         openSnackbar({
  //           message:
  //             "Solicitação de conversão reagendada. Volte daqui alguns minutos.",
  //           severity: "info",
  //         });
  //         setTimeout(() => {
  //           setLoadingFile(false);
  //         }, 1000);
  //       } else {
  //         setLoadingFile(false);
  //       }
  //     } catch (err) {
  //       openSnackbar({
  //         message:
  //           "Não foi possível efetuar a conversão, tente novamente ou fale com o suporte.",
  //         severity: "error",
  //       });
  //     }
  //   },
  //   [openSnackbar]
  // );

  const handleDownloadAll = async (obj: any) => {
    obj.forEach((item) => {
      handleDownloadFile(item.file_id);
    });
    scrollTop();
  };

  React.useEffect(() => {
    handleDownloads();
  }, [handleDownloads]);

  return (
    <Container maxWidth={false}>
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        sx={{ marginBottom: 5, overflowX: "auto" }}
      >
        <Typography variant="h5" component="h4" fontWeight="bold">
          Meus Downloads
        </Typography>
      </Grid>
      <Paper
        sx={{
          width: "100%",
          borderRadius: 4,
          boxShadow:
            "rgb(145 158 171 / 20%) 0px 0px 2px 0px, rgb(145 158 171 / 12%) 0px 12px 24px -4px",
        }}
      >
        {!loading ? (
          <TableContainer>
            {!readOnly &&
              downloads &&
              Object.keys(downloads).map((index) => {
                const obj: any = downloads[index];
                return (
                  <Table aria-label="sticky table" key={`idx${index}`}>
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ width: "30px" }}>
                          <IconButton
                            aria-label="expand row"
                            size="small"
                            onClick={() => setOpen(index)}
                          >
                            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          Solicitação de arquivos em:{" "}
                          <b>
                            {obj && obj[0] && obj[0].created_at
                              ? `${formatDate(
                                  obj[0].created_at,
                                  "dd/MM/yyyy"
                                )} às `
                              : ""}
                            {obj && obj[0] && obj[0].created_at
                              ? `${formatDate(obj[0].created_at, "HH:mm:ss")}`
                              : ""}
                          </b>
                          &nbsp; &nbsp;
                          <Button
                            color="primary"
                            size="small"
                            onClick={() => handleDownloadAll(obj)}
                          >
                            Baixar todos disponíveis
                          </Button>
                        </TableCell>
                        <TableCell align="right"></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableCell colSpan={3}>
                        <Collapse
                          in={open === index}
                          timeout="auto"
                          unmountOnExit
                        >
                          <TableContainer>
                            <Table aria-label="sticky table">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Arquivo</TableCell>
                                  <TableCell>Empresa</TableCell>
                                  <TableCell>Solicitado em</TableCell>
                                  <TableCell>Solicitante</TableCell>
                                  <TableCell>Disponível até</TableCell>
                                  <TableCell>Câmera</TableCell>
                                  <TableCell>Gravado em</TableCell>
                                  <TableCell>Situação</TableCell>
                                  <TableCell width="20%">Ações</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {obj &&
                                  obj.length > 0 &&
                                  obj.map(
                                    (
                                      video: IQueuedFileDownload,
                                      index2: number
                                    ) => (
                                      <TableRow
                                        hover
                                        role="checkbox"
                                        tabIndex={-1}
                                        key={`idx${index2}`}
                                      >
                                        <TableCell>
                                          {video.file.name}
                                          {!video.expire_at && (
                                            <LinearProgress color="primary" />
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {video.file.company.fantasy_name ||
                                            "Não definido"}
                                          {!video.expire_at && (
                                            <LinearProgress color="primary" />
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {video && video.created_at && (
                                            <>
                                              {formatDate(
                                                video.created_at,
                                                "dd/MM/yyyy HH:mm:ss"
                                              )}
                                            </>
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {`${video.user.name} ${video.user.surname}`}
                                        </TableCell>
                                        <TableCell>
                                          {video && video.expire_at
                                            ? formatDate(
                                                video.expire_at,
                                                "dd/MM/yyyy"
                                              )
                                            : "Processando"}
                                        </TableCell>

                                        <TableCell>
                                          {video.file?.camera?.name ||
                                            "Indefinido"}
                                        </TableCell>
                                        <TableCell>
                                          {video.file.recording_dvr_date
                                            ? formatDate(
                                                video.file.recording_dvr_date,
                                                "dd/MM/yyyy HH:mm:ss"
                                              )
                                            : "Indefinido"}
                                        </TableCell>
                                        <TableCell>
                                          <Chip
                                            label={
                                              statusDisplay[video.status] ||
                                              "Indefinido"
                                            }
                                            color={`${
                                              video.status === "COMPLETED"
                                                ? "success"
                                                : "warning"
                                            }`}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <Tooltip
                                            title={
                                              video.status !== "COMPLETED"
                                                ? "Em progresso..."
                                                : "Baixar"
                                            }
                                          >
                                            {!loadingFile ? (
                                              <Button
                                                disabled={
                                                  video.status !== "COMPLETED"
                                                }
                                                color="primary"
                                                size="small"
                                                aria-label={
                                                  video.status !== "COMPLETED"
                                                    ? "Em progresso..."
                                                    : "Baixar"
                                                }
                                                style={{ minWidth: "24px" }}
                                                // onClick={() =>
                                                //   handleDownloadFile(
                                                //     video.file_id
                                                //   )
                                                // }
                                                onClick={() =>
                                                  handleVideoOpen(
                                                    video.url,
                                                    video.file.name,
                                                    video,
                                                    true,
                                                    video.file.id
                                                  )
                                                }
                                              >
                                                <SystemUpdateAltOutlined />
                                              </Button>
                                            ) : (
                                              <CircularProgress size={15} />
                                            )}
                                          </Tooltip>
                                          {
                                            <>
                                              {video?.file?.fileType
                                                ?.is_video ? (
                                                <IconButton
                                                  disabled={
                                                    video.status !== "COMPLETED"
                                                  }
                                                  color="primary"
                                                  size="small"
                                                  aria-label={"Reproduzir"}
                                                  onClick={() =>
                                                    handleVideoOpen(
                                                      video.url,
                                                      video.file.name,
                                                      video,
                                                      false,
                                                      video.file_id
                                                    )
                                                  }
                                                >
                                                  <Tooltip title={"Reproduzir"}>
                                                    <OndemandVideoOutlined />
                                                  </Tooltip>
                                                </IconButton>
                                              ) : (
                                                <></>
                                              )}
                                              {["pdf"].includes(
                                                video?.file?.fileType.uid.toLocaleLowerCase()
                                              ) ? (
                                                <IconButton
                                                  color="primary"
                                                  size="small"
                                                  aria-label={"Visualizar"}
                                                  onClick={() =>
                                                    handlePDFOpen(video.file.id)
                                                  }
                                                >
                                                  <Tooltip title={"Visualizar"}>
                                                    <PictureAsPdfOutlined />
                                                  </Tooltip>
                                                </IconButton>
                                              ) : (
                                                <></>
                                              )}
                                              {[
                                                "jpg",
                                                "jpeg",
                                                "png",
                                                "gif",
                                              ].includes(
                                                video?.file?.fileType.uid.toLocaleLowerCase()
                                              ) ? (
                                                <IconButton
                                                  color="primary"
                                                  size="small"
                                                  aria-label={"Visualizar"}
                                                  onClick={() =>
                                                    handleImgOpen(video.file.id)
                                                  }
                                                >
                                                  <Tooltip title={"Visualizar"}>
                                                    <ImageOutlined />
                                                  </Tooltip>
                                                </IconButton>
                                              ) : (
                                                <></>
                                              )}
                                            </>
                                          }
                                          {video.status === "COMPLETED" && (
                                            <>
                                              {/*<IconButton
                                                color="primary"
                                                size="small"
                                                aria-label="Solicitar conversão"
                                                disabled={!!video.mp4_url}
                                                onClick={() =>
                                                  handleConversion(video.id)
                                                }
                                              >
                                                <Tooltip title="Solicitar conversão">
                                                  <FastForwardOutlined />
                                                </Tooltip>
                                              </IconButton>*/}
                                              {/*<IconButton
                                                color="primary"
                                                size="small"
                                                aria-label={
                                                  !video.mp4_url
                                                    ? "Convertendo..."
                                                    : "Assistir"
                                                }
                                                disabled={!video.mp4_url}
                                                onClick={() =>
                                                  handleVideoOpen(
                                                    video.file.id,
                                                    video.file.name,                                                    
                                                    video
                                                  )
                                                }
                                              >
                                                <Tooltip
                                                  title={
                                                    !video.mp4_url
                                                      ? "Convertendo..."
                                                      : "Assistir"
                                                  }
                                                >
                                                  <OndemandVideoOutlined />
                                                </Tooltip>
                                              </IconButton>*/}
                                              {/*<IconButton
                                                color="primary"
                                                size="small"
                                                aria-label={
                                                  !video.mp4_url
                                                    ? "Convertendo..."
                                                    : "Baixar MP4"
                                                }
                                                disabled={!video.mp4_url}
                                                onClick={() =>
                                                  window.open(video.mp4_url)
                                                }
                                              >
                                                <Tooltip
                                                  title={
                                                    !video.mp4_url
                                                      ? "Convertendo..."
                                                      : "Baixar MP4"
                                                  }
                                                >
                                                  <CloudDownloadOutlined />
                                                </Tooltip>
                                              </IconButton>*/}
                                            </>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    )
                                  )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Collapse>
                      </TableCell>
                    </TableBody>
                  </Table>
                );
              })}
          </TableContainer>
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "20px",
            }}
          >
            <CircularProgress size={30} />
          </div>
        )}

        <Player
          open={videoOpen}
          videoName={videoName}
          videoURL={videoURL}
          onClose={() => resetVideoOpen()}
        />

        {loadingVideo && (
          <div
            style={{
              width: "100%",
              height: "100%",
              position: "absolute",
              top: 0,
              left: 0,
              zIndex: 10,
              background: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#FFF",
              fontSize: "30px",
              fontWeight: "bold",
              gap: 30,
            }}
          >
            <div>
              <CircularProgress size={80} />
            </div>
            <div>Solicitando, aguarde...</div>
          </div>
        )}
      </Paper>
      {pdfURL && pdfURL.length > 0 && (
        <PDFViewer url={pdfURL} onClose={() => setPDFURL("")} />
      )}
      {imgURL && imgURL.length > 0 && (
        <ImagePreview url={imgURL} onClose={() => setImgURL("")} />
      )}
    </Container>
  );
};

export { QueuedDownloads };
